import React, { useState } from 'react';
import { Link, Row, Stack, Text, Title } from 'library/ui';
import { ArrowForward } from '@mui/icons-material';
import { format12HourTime, formatMonthDay, formatShortMonthDayYear } from 'shared/Dates';

import ChangeModal from 'parent-portal/subs/ConfirmSubHours/ChangeModal';
import { ClockInModal } from 'parent-portal/jobs/upcoming/BusinessCalendar/ShiftCard/ClockInButton';
import { BasicCard } from './BasicCard';
import { WorkerTab } from './WorkerTab';

import type { IHourReport } from 'internal/payments/HourReportModels';
import type { DashboardSummary, IWorkerTab, UnclockedInTimesheetWorker } from '../types';
import { groupBy } from 'shared/arrays';
import { useHistory } from 'react-router';
import { getTotalHours } from 'shared';

interface ExtendedWorkerTab extends IWorkerTab {
    hour_report: IHourReport | null;
    worker: DashboardSummary['timesheets'][0];
}

export function Timesheets({
    dashboardSummary,
    loading,
    refresh,
}: {
    dashboardSummary: DashboardSummary;
    loading: boolean;
    refresh: () => void;
}) {
    const { timesheets, actionable_timesheets: actionableTimesheets } = dashboardSummary;
    const history = useHistory();
    const [changeReport, setChangeReport] = useState<IHourReport | undefined>(undefined);
    const [clockInWorker, setClockInWorker] = useState<DashboardSummary['timesheets'][0] | undefined>(undefined);

    const hasTimesheets = !!timesheets && timesheets.length > 0;
    const groupedTimesheets = groupBy(timesheets, 'location_name');

    const mostTimesheets: UnclockedInTimesheetWorker[] = Object.values(groupedTimesheets).sort(
        (a, b) => b.length - a.length,
    )[0];

    const workers: ExtendedWorkerTab[] = hasTimesheets
        ? mostTimesheets.map((worker: UnclockedInTimesheetWorker) => ({
              user: worker.user,
              profile_image: worker.profile_image,
              ...middleAndBottomText(worker),
              hour_report: worker.hour_report,
              worker,
          }))
        : [];

    const actionRequiredworkers =
        actionableTimesheets?.map((worker) => ({
            user: worker.user,
            profile_image: worker.profile_image,
            ...middleAndBottomText(worker),
            hour_report: worker.hour_report,
            worker,
            business_location_id: worker.business_location_id,
        })) || [];

    const sentHomeWorkers = dashboardSummary.sent_home_workers?.map((worker) => ({
        user: worker.user,
        profile_image: worker.user.profile_picture,
        middle: <Text size="xs">Sent Home during shift on {formatMonthDay(worker.shift_date)}</Text>,
        business_location_id: worker.business_location.id,
        worker,
    }));

    function middleAndBottomText(worker: DashboardSummary['timesheets'][0]) {
        if (!worker.hour_report || !worker.hour_report.start)
            return {
                middle: (
                    <Text size="xs" weight="bold">
                        Not clocked in
                    </Text>
                ),
            };

        if (worker.hour_report.end) {
            return {
                middle: (
                    <Text size="xs" weight="bold">
                        {formatMonthDay(worker.hour_report.start)},{' '}
                        {format12HourTime(worker.hour_report.start, worker.timezone)} -{' '}
                        {format12HourTime(worker.hour_report.end, worker.timezone)}
                    </Text>
                ),
                bottom: worker.hour_report.approved_break ? (
                    <Text size="xs">{worker.hour_report.approved_break} minute break</Text>
                ) : (
                    <Text size="xs">No break taken</Text>
                ),
            };
        }

        const clockedIn = {
            middle: (
                <Text size="xs">
                    <b>Clock In:</b> {formatMonthDay(worker.hour_report.start)},{' '}
                    {format12HourTime(worker.hour_report.start, worker.timezone)}
                </Text>
            ),
        };

        if (worker.hour_report.approved_break) {
            return {
                ...clockedIn,
                bottom: <Text size="xs">{worker.hour_report.approved_break} minute break</Text>,
            };
        }

        return clockedIn;
    }

    const noDataToShow =
        !hasTimesheets &&
        !actionRequiredworkers.length &&
        !dashboardSummary.sent_home_workers.length &&
        !dashboardSummary.missing_payment_reports?.length;

    if (noDataToShow) {
        return null;
    }

    return (
        <BasicCard loading={loading}>
            <Stack gap="md">
                <Row justify="between">
                    <Title size="h2">Timesheets</Title>
                    {hasTimesheets && (
                        <Link to="/timesheets" size="sm" underline={false}>
                            View All
                        </Link>
                    )}
                </Row>
                {actionRequiredworkers?.length ? (
                    <Stack gap="sm">
                        <Text weight="bold" tone="accent">
                            Action Required: Manual Hours Reported
                        </Text>
                        <Text size="sm" tone="secondary">
                            Hours were reported by workers after they left your center and need your approval.
                        </Text>
                        <Stack gap="sm">
                            {actionRequiredworkers.map((x) => (
                                <WorkerTab
                                    key={x.user.id}
                                    worker={x}
                                    onClick={() => history.push(`/timesheets?location=${x.business_location_id}`)}
                                    icon={ArrowForward}
                                />
                            ))}
                        </Stack>
                    </Stack>
                ) : null}
                {sentHomeWorkers?.length ? (
                    <Stack gap="sm">
                        <Text weight="bold" tone="accent">
                            Sent Home Workers
                        </Text>
                        <Text size="sm" tone="secondary">
                            Workers were sent home and need your approval.
                        </Text>
                        <Stack gap="sm">
                            {sentHomeWorkers.map((worker) => (
                                <WorkerTab
                                    key={worker.user.id}
                                    worker={worker}
                                    onClick={() => history.push(`/timesheets?location=${worker.business_location_id}`)}
                                    icon={ArrowForward}
                                />
                            ))}
                        </Stack>
                    </Stack>
                ) : null}
                {dashboardSummary.missing_payment_reports?.length ? (
                    <Stack gap="sm">
                        <Text weight="bold" tone="accent">
                            Missing Payment Reports
                        </Text>
                        <Stack gap="sm">
                            {dashboardSummary.missing_payment_reports?.map((report) => (
                                <WorkerTab
                                    key={report.id}
                                    worker={{
                                        ...report,
                                        middle: (
                                            <Text size="xs">
                                                <b>{formatShortMonthDayYear(report.start_date)}</b> (
                                                {getTotalHours(report).toFixed(2)} hours)
                                            </Text>
                                        ),
                                    }}
                                    attention
                                    onClick={() => history.push(`/timesheets?location=${report.business_location.id}`)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                ) : null}
                {hasTimesheets && (
                    <Stack gap="sm">
                        <Text weight="bold">
                            {mostTimesheets[0].location_name} ({mostTimesheets.length})
                        </Text>
                        <Stack gap="sm">
                            {workers.map((worker: ExtendedWorkerTab) => (
                                <WorkerTab
                                    key={worker.user.id}
                                    worker={worker}
                                    onClick={() =>
                                        worker.hour_report
                                            ? setChangeReport({
                                                  ...worker.hour_report,
                                                  timezone: worker.worker.timezone,
                                              })
                                            : setClockInWorker(worker.worker)
                                    }
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}
            </Stack>

            {changeReport && (
                <ChangeModal
                    changeReport={changeReport}
                    setChangeReport={setChangeReport}
                    updateHoursReported={refresh}
                />
            )}
            {clockInWorker && (
                <ClockInModal
                    open={!!clockInWorker}
                    onClose={() => setClockInWorker(undefined)}
                    onComplete={refresh}
                    appointmentId={clockInWorker.appointment_id}
                    startDate={clockInWorker.start_date}
                    providerName={`${clockInWorker.user.first_name} ${clockInWorker.user.last_name}`}
                    providerUserId={clockInWorker.user.id}
                    timezone={clockInWorker.timezone}
                />
            )}
        </BasicCard>
    );
}
